import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { isStringArray } from '@/api/queries/utils';
import FiltersContext, { OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { rehydrateFilterState, getDefaultFiltersState } from '@/context/FiltersContext/FiltersContextProvider';
import { useGetUserCountries } from '@/api/queries/country/useGetUserCountries';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import { globalFilters } from '@/models/filter/globalFilterAllowlist';
import { UserFilterMetadata } from '@/models/user/userFilter';
import { useToast } from '@/storybook/components/Toast/Toast';
import { FilterType } from '@/models/filter/FilterType';
import type { FilterState } from '@/types/filter/filterState';
import applyLocalFiltersToStorage from '@/utils/filter/applyLocalFiltersToStorage';
import { AppliedFilterSetStorage } from '@/utils/filter/appliedFilterSetStorage';
import { writeCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { resolveCountryLabel } from '@/models/filter/utils/countryFilterHelpers';
import { isRecord } from '@/utils/filter/isRecord';
import type { SelectItem } from '@/types/ui/selectItem';
import useSharedViewValidation, {
  SharedViewScenario,
  SharedViewValidationResult,
} from '@/hooks/useSharedViewValidation';
import { SharedViewDialogProps } from '@/components/filter/SharedViewDialog/SharedViewDialog';
import {
  deriveSenderCountryCodesFromLocale,
  isSharedViewPayload,
  isSharedViewFilterState,
  recalculatePresetDateRanges,
} from './useApplySharedView.utils';
import { migrateSharedViewKeys } from '@/utils/filter/filterKeyMigrations';
import { SHARE_VIEW_PARAM } from '@/utils/filter/shareView';

interface SharedViewDialogState {
  scenario: SharedViewScenario;
  validation: SharedViewValidationResult;
  localFiltersData?: Record<string, Record<string, unknown>>;
  filterMetadata?: UserFilterMetadata;
  optionPaths?: OptionPathsByFilter;
  senderCountryLabel?: string;
  recipientCountryLabel?: string;
}

export interface UseApplySharedViewResult {
  isLoading: boolean;
  sharedViewDialogProps: SharedViewDialogProps | null;
}

const buildErrorDialogState = (defaultFiltersState: FilterState): SharedViewDialogState => ({
  scenario: SharedViewScenario.ERROR,
  validation: {
    scenario: SharedViewScenario.ERROR,
    validFilters: defaultFiltersState,
    missingFilters: [],
    senderCountryCodes: [],
    recipientCountryCodes: [],
  },
});

const useApplySharedView = (): UseApplySharedViewResult => {
  const { t } = useTranslation();
  const { handleApplyFilter, handleClearAllFilters, filterConfig, defaultFiltersState, replaceOptionPaths } =
    useContext(FiltersContext);
  const { makeToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { validateSharedFilters } = useSharedViewValidation();
  const { data: userCountries = [] } = useGetUserCountries<SelectItem<string>[]>();
  const isApplying = useRef(false);
  const processedId = useRef<string | null>(null);
  const shareViewId = searchParams.get(SHARE_VIEW_PARAM);
  const [isLoading, setIsLoading] = useState(!!shareViewId);
  const [dialogState, setDialogState] = useState<SharedViewDialogState | null>(null);

  const clearShareViewParam = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete(SHARE_VIEW_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const applyFilters = useCallback(
    (
      filters: FilterState,
      localFiltersData?: Record<string, Record<string, unknown>>,
      filterMetadata?: UserFilterMetadata,
      optionPaths?: OptionPathsByFilter,
      wasModified = false,
    ) => {
      const localFiltersPayload = localFiltersData ?? {};

      const config = filterConfig ?? globalFilters;
      const defaultState = getDefaultFiltersState(config);
      const rehydratedFilters = rehydrateFilterState(filters, defaultState);

      // The registry moves to the shared selection only on a confirmed apply,
      // never during fetch/validation — otherwise a cancelled or errored dialog
      // would leave the paths ahead of the still-applied filter state. A
      // whole-filter replacement without paths clears the registry so a stale
      // hierarchy from a previous selection is not read into the new query.
      if (replaceOptionPaths) {
        replaceOptionPaths(optionPaths ?? {});
      }

      applyLocalFiltersToStorage(localFiltersPayload);
      handleApplyFilter(rehydratedFilters);

      // Re-apply after effects settle (prune/auto-select in page filters)
      setTimeout(() => applyLocalFiltersToStorage(localFiltersPayload), 500);

      if (filterMetadata) {
        AppliedFilterSetStorage.save(filterMetadata, rehydratedFilters, localFiltersPayload);
      }

      makeToast({
        content: String(t(wasModified ? 'global.filters.shareViewModifiedApplied' : 'global.filters.shareViewApplied')),
        appearance: 'success',
        autoClose: 5000,
      });
    },
    [handleApplyFilter, filterConfig, replaceOptionPaths, makeToast, t],
  );

  const handleDialogApply = useCallback(() => {
    if (!dialogState) return;

    if (dialogState.scenario === SharedViewScenario.ERROR) {
      handleClearAllFilters();
    } else {
      const wasModified = dialogState.scenario === SharedViewScenario.VALUES_NOT_FOUND;
      applyFilters(
        dialogState.validation.validFilters,
        dialogState.localFiltersData,
        dialogState.filterMetadata,
        dialogState.optionPaths,
        wasModified,
      );
    }

    setDialogState(null);
    clearShareViewParam();
  }, [dialogState, applyFilters, handleClearAllFilters, clearShareViewParam]);

  const handleDialogCancel = useCallback(() => {
    setDialogState(null);
    clearShareViewParam();
  }, [clearShareViewParam]);

  const applySharedFilters = useCallback(
    async (filterId: string) => {
      if (isApplying.current) return;
      isApplying.current = true;
      setIsLoading(true);

      try {
        const response = await UserFilterApiService.getFilterById(filterId);
        const { filters_download_link: downloadLink } = response.data;

        // Validate that the download URL points to an expected S3 origin.
        // The auth interceptor is intentionally skipped for pre-signed S3 URLs.
        const downloadUrl = new URL(downloadLink);
        if (!downloadUrl.hostname.endsWith('.amazonaws.com')) {
          throw new Error('Invalid download URL');
        }

        const filtersResponse = await axios.get<Record<string, unknown>>(downloadLink);
        const responseData = filtersResponse.data;

        // Unwrap legacy S3 envelope: { filters: {...}, userId, locale }
        const hasFiltersEnvelope = 'filters' in responseData && isRecord(responseData.filters);
        const rawUnwrappedData = hasFiltersEnvelope ? responseData.filters : responseData;

        // Migrate legacy global-filter keys: renamed keys are mapped to their
        // current FilterType value and removed keys are dropped. Without this,
        // views saved by older app versions fail the strict payload guards
        // (surfacing as "Cannot load view") or silently lose renamed filters.
        const unwrappedData = migrateSharedViewKeys(rawUnwrappedData);

        let rawGlobalFilters: FilterState;
        let localFiltersData: Record<string, Record<string, unknown>> | undefined;
        let senderCountryCodes: string[] = [];
        let optionPaths: OptionPathsByFilter | undefined;

        if (isSharedViewPayload(unwrappedData)) {
          rawGlobalFilters = unwrappedData.globalFilters;
          localFiltersData = unwrappedData.localFilters;
          senderCountryCodes = unwrappedData.senderCountryCodes ?? [];

          if (senderCountryCodes.length === 0 && !unwrappedData.resolvedCountryCodes?.length) {
            senderCountryCodes = deriveSenderCountryCodesFromLocale(response.data.filterCountries ?? '');
          }

          if (unwrappedData.resolvedCountryCodes?.length) {
            writeCachedCountryCodes(unwrappedData.resolvedCountryCodes);
          }

          // Carried through to applyFilters, which moves the registry to these
          // paths only on a confirmed apply (not while a dialog is pending).
          optionPaths = unwrappedData.optionPaths;
        } else if (isSharedViewFilterState(unwrappedData) && Object.keys(unwrappedData).length > 0) {
          // Backward compatibility: flat FilterState
          rawGlobalFilters = unwrappedData;
        } else {
          throw new Error('Invalid filter payload');
        }

        // Recalculate preset date ranges (YTD, Monthly, etc.) relative to
        // today so the recipient sees the current period.
        rawGlobalFilters = recalculatePresetDateRanges(rawGlobalFilters);

        // Validate shared filters against recipient's available options
        const resolvedCountryCodes = isSharedViewPayload(unwrappedData)
          ? unwrappedData.resolvedCountryCodes
          : undefined;
        const validation = await validateSharedFilters(
          rawGlobalFilters,
          senderCountryCodes,
          localFiltersData,
          resolvedCountryCodes,
        );

        // Shared filters should not be stored as applied filter sets —
        // only saved_filter types show as a filter set chip in the filter bar.
        const metadata = response.data.filter?.filterType === 'saved_filter' ? response.data.filter : undefined;

        if (validation.scenario === SharedViewScenario.APPLY) {
          applyFilters(
            validation.validFilters,
            validation.validLocalFilters ?? localFiltersData,
            metadata,
            optionPaths,
          );
          clearShareViewParam();
        } else {
          const countryFilter = rawGlobalFilters[FilterType.COUNTRY];
          const senderFilterValues = isStringArray(countryFilter) ? countryFilter : undefined;
          setDialogState({
            scenario: validation.scenario,
            validation,
            localFiltersData: validation.validLocalFilters ?? localFiltersData,
            filterMetadata: metadata,
            optionPaths,
            senderCountryLabel: resolveCountryLabel(
              validation.senderCountryCodes,
              userCountries,
              t,
              senderFilterValues,
            ),
            recipientCountryLabel: resolveCountryLabel(validation.recipientCountryCodes, userCountries, t),
          });
        }
      } catch {
        setDialogState(buildErrorDialogState(defaultFiltersState));
      } finally {
        isApplying.current = false;
        setIsLoading(false);
      }
    },
    [validateSharedFilters, applyFilters, defaultFiltersState, userCountries, t, clearShareViewParam],
  );

  useEffect(() => {
    if (shareViewId && processedId.current !== shareViewId) {
      processedId.current = shareViewId;
      applySharedFilters(shareViewId);
    }
  }, [shareViewId, applySharedFilters]);

  const sharedViewDialogProps: SharedViewDialogProps | null = dialogState
    ? {
        isOpen: true,
        scenario: dialogState.scenario,
        missingFilters: dialogState.validation.missingFilters,
        senderCountryLabel: dialogState.senderCountryLabel,
        recipientCountryLabel: dialogState.recipientCountryLabel,
        onApply: handleDialogApply,
        onCancel: dialogState.scenario === SharedViewScenario.ERROR ? handleDialogApply : handleDialogCancel,
      }
    : null;

  return { isLoading, sharedViewDialogProps };
};

export default useApplySharedView;
